import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignaturePad from "@/components/SignaturePad";
import { Sparkle, Check, Loader2, Vote, Upload, PenTool, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Lounge = () => {
  const { token } = useParams();
  const [circle, setCircle] = useState(null);
  const [error, setError] = useState("");
  const [voterName, setVoterName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);
  const padRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/lounge/${token}`);
      setCircle(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "This lounge link is invalid or expired.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  // live-ish updates while others vote/sign
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  const vote = async (cardId) => {
    if (!voterName.trim()) { setError("Enter your name to vote."); return; }
    setError("");
    await axios.post(`${API}/lounge/${token}/vote`, { card_id: cardId, voter_name: voterName });
    if (navigator.vibrate) navigator.vibrate(20);
    load();
  };

  const uploadInspiration = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    await axios.post(`${API}/lounge/${token}/inspiration`, { image_base64: b64, by: voterName || "A member" });
    load();
  };

  const sign = async () => {
    const img = padRef.current?.getDataURL();
    if (!voterName.trim()) { setError("Enter your name."); return; }
    if (!img) { setError("Add your signature."); return; }
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/lounge/${token}/sign`, { name: voterName, note, image_base64: img });
      setSigned(true);
      if (navigator.vibrate) navigator.vibrate([20, 40, 20]);
      load();
    } finally { setBusy(false); }
  };

  if (error && !circle) return <div className="min-h-screen flex items-center justify-center text-red-500 p-6" data-testid="lounge-error">{error}</div>;
  if (!circle) return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Entering the Royal Lounge…</div>;

  return (
    <div className="min-h-screen py-10 px-4" data-testid="lounge-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Sparkle className="w-5 h-5 text-white" /></div>
          <span className="gifted-brand text-2xl">GIFTED</span>
        </div>
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-2">The Royal Lounge</p>
          <h1 className="font-serif text-4xl">A {circle.occasion} for {circle.loved_one_name}</h1>
          <p className="text-muted-foreground mt-2">Vote on a design, add inspiration, and sign the card together.</p>
        </div>

        <div className="max-w-sm mx-auto mb-8">
          <input data-testid="lounge-name" value={voterName} onChange={(e) => setVoterName(e.target.value)}
            className="w-full bg-input border border-border rounded-2xl px-4 py-3 text-center focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Your name" />
        </div>

        {circle.status === "generating" ? (
          <div className="text-center py-12 text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> The designs are being curated…</div>
        ) : (
          <section className="mb-10">
            <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Vote className="w-5 h-5 text-primary" /> Vote on a design</h2>
            <div className="grid sm:grid-cols-3 gap-5" data-testid="lounge-candidates">
              {circle.candidates.map((c) => {
                const tally = (circle.votes?.[c.id] || []).length;
                const leading = circle.leading_card_id === c.id;
                return (
                  <div key={c.id} className={`surface rounded-[1.5rem] overflow-hidden card-soft ${leading ? "ring-2 ring-yellow-500/50" : ""}`} data-testid="lounge-candidate">
                    <img src={`data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-40 object-cover" />
                    <div className="p-4">
                      {leading && <div className="text-[10px] gold-text mb-1">Leading choice</div>}
                      <button data-testid="lounge-vote-btn" onClick={() => vote(c.id)}
                        className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-2.5 hover:opacity-90">
                        Vote {tally > 0 ? `· ${tally}` : ""}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-center">
              <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <Upload className="w-4 h-4" /> Add an inspiration image ({circle.inspiration_count})
                <input type="file" accept="image/*" className="hidden" onChange={uploadInspiration} data-testid="lounge-inspiration" />
              </label>
            </div>
          </section>
        )}

        <section className="surface rounded-[1.75rem] p-6 max-w-lg mx-auto">
          <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><PenTool className="w-5 h-5 text-primary" /> Sign the card</h2>
          {signed ? (
            <div className="text-center py-6 text-secondary flex flex-col items-center gap-2" data-testid="lounge-signed">
              <Check className="w-8 h-8" /> Your signature was added. Thank you!
            </div>
          ) : (
            <>
              <SignaturePad ref={padRef} label="Sign with your finger or stylus" />
              <input data-testid="lounge-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={60}
                className="w-full mt-3 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="A one-line note (optional)" />
              {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
              <button data-testid="lounge-sign-btn" onClick={sign} disabled={busy}
                className="w-full mt-3 bg-gradient-to-r from-secondary to-primary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : <><Users className="w-4 h-4" /> Add My Signature</>}
              </button>
            </>
          )}
          {circle.signatures?.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground text-center">
              Signed by {circle.signatures.map((s) => s.name).join(", ")}
            </div>
          )}
        </section>
        <p className="text-center text-xs text-muted-foreground mt-6">Only the Royal Owner can finalize &amp; mail this card.</p>
      </div>
    </div>
  );
};

export default Lounge;
